@file:Suppress("TooManyFunctions", "UnsafeCastFromDynamic")

package top.wcpe.allincore.ui.web.model

import top.wcpe.allincore.ui.runtime.event.HitRegistration
import top.wcpe.allincore.ui.runtime.event.SessionHitRegistry
import top.wcpe.allincore.ui.web.ModelCompositionHost
import top.wcpe.allincore.ui.web.ModelCompositionRequest
import top.wcpe.allincore.ui.web.ModelCompositionResult
import top.wcpe.allincore.ui.web.ModelTextureLease
import top.wcpe.allincore.ui.web.Texture
import top.wcpe.allincore.ui.web.WebInputOwnerHandler
import top.wcpe.allincore.ui.web.WebRenderDiagnostic
import top.wcpe.allincore.ui.web.WebRenderDiagnosticCapabilityKeys
import top.wcpe.allincore.ui.web.WebRenderDiagnosticSeverity
import top.wcpe.allincore.ui.web.WebRenderDiagnosticSink
import top.wcpe.allincore.ui.web.canonicalWebResourceKey
import top.wcpe.allincore.ui.web.defaultWebRenderDiagnosticSink
import kotlin.js.Promise
import kotlin.js.json
import kotlin.math.PI
import kotlin.math.max
import kotlin.math.roundToInt
import kotlin.math.sqrt

internal fun interface ThreeRenderCanvasFactory {
    fun create(): dynamic
}

internal interface ThreeModelOutputFactory {
    fun create(width: Int, height: Int, rgbaPixels: dynamic, leaseId: String): ModelTextureLease
}

internal data class ThreeModelViewState(
    val rotationX: Float,
    val rotationY: Float,
)

internal data class ThreeModelBundleKey(
    val source: String,
    val texture: String?,
    val modelBase: String,
    val canonicalPath: String,
    val contentVersion: String,
)

internal class ThreeModelCompositionHost(
    private val moduleLoader: ThreeModuleLoader,
    private val assetLoader: ThreeModelAssetLoader,
    private val diagnostics: WebRenderDiagnosticSink = defaultWebRenderDiagnosticSink(),
    private val renderCanvasFactory: ThreeRenderCanvasFactory = ThreeRenderCanvasFactory(::createRenderCanvas),
    private val outputFactory: ThreeModelOutputFactory = BrowserThreeModelOutputFactory,
    private val backendSubmissionObserver: () -> Unit = {},
    private val onViewOffsetChanged: () -> Unit = {},
) : ModelCompositionHost, ThreeStaticModelPreparer {
    private val instances = mutableMapOf<String, InstanceRecord>()
    private val outputLeases = mutableSetOf<TrackedOutputLease>()
    private var renderQueue = Promise.resolve(Unit)
    private var rendererState: RendererState? = null
    private var lostCanvas: LostCanvas? = null
    private var disposed = false
    private var assetLoaderDisposed = false
    private var contextLost = false
    private var epoch = 0
    private var outputSequence = 0L
    private var lastRequest: ModelCompositionRequest? = null
    private var activePreparedModels: ThreePreparedModelSet? = null
    private var hitRegistry: SessionHitRegistry<WebInputOwnerHandler>? = null

    override fun compose(request: ModelCompositionRequest): Promise<ModelCompositionResult> {
        lastRequest = request
        if (disposed) return Promise.resolve(ModelCompositionResult.Cancelled)
        if (contextLost) return Promise.resolve(contextPlaceholder(request))
        val record = instances.getOrPut(request.instanceKey, ::InstanceRecord)
        syncViewOffsetIdentity(record, request)
        syncHitRegistration(record, request)
        val serial = ++record.serial
        val capturedEpoch = epoch
        val prepared = activePreparedModels?.entry(request.staticModelRequest())
        val render = if (prepared == null) prepareRuntime(request, record, serial, capturedEpoch) else {
            Promise.resolve(acceptPrepared(request, record, prepared, serial, capturedEpoch))
        }
        return render.thenPromise(::enqueueRender).catch { reason -> failureResult(request, reason) }
    }

    override fun bindHitRegistry(registry: SessionHitRegistry<WebInputOwnerHandler>?) {
        if (hitRegistry === registry) return
        clearHitRegistrations()
        hitRegistry = registry
    }

    /** 读取实例当前视角偏移；无实例时返回 0/0。 */
    fun viewOffsets(instanceKey: String): ThreeModelViewState {
        val record = instances[instanceKey] ?: return ThreeModelViewState(0f, 0f)
        return ThreeModelViewState(record.viewOffsetX, record.viewOffsetY)
    }

    /** 累加视角偏移（owner 拖拽）；返回更新后的偏移。 */
    fun applyViewOffset(instanceKey: String, deltaX: Float, deltaY: Float): ThreeModelViewState {
        val record = instances[instanceKey] ?: return ThreeModelViewState(0f, 0f)
        record.viewOffsetX += deltaX
        record.viewOffsetY += deltaY
        onViewOffsetChanged()
        return ThreeModelViewState(record.viewOffsetX, record.viewOffsetY)
    }

    override fun prepareStaticModels(requests: List<ThreeStaticModelRequest>): Promise<ThreePreparedModelSet> {
        if (disposed) return rejectedPromise(HostFailure(CANCELLED, "模型合成宿主已销毁"))
        if (requests.isEmpty()) return Promise.resolve(emptyPreparedModelSet())
        return loadModule().thenPromise { module ->
            ThreePreparedModelBuilder(module, assetLoader, ::activatePrepared, ::replacePrepared).prepare(requests)
        }
    }

    private fun emptyPreparedModelSet(): ThreePreparedModelSet =
        ThreePreparedModelSet(emptyList(), ::activatePrepared, ::replacePrepared)

    private fun prepareRuntime(
        request: ModelCompositionRequest,
        record: InstanceRecord,
        serial: Int,
        capturedEpoch: Int,
    ): Promise<PreparedRender> = loadModel(request).thenPromise { asset ->
        prepareBundle(request, record, serial, capturedEpoch, asset)
    }

    override fun dispose() {
        if (!disposed) disposeHostResources()
        disposeAssetLoader()
    }

    private fun disposeHostResources() {
        disposed = true
        epoch += 1
        hitRegistry = null
        disposeOutputLeases()
        activePreparedModels?.dispose()
        activePreparedModels = null
        disposeInstances()
        disposeRendererState()
        lostCanvas?.detach()
        lostCanvas = null
        contextLost = false
    }

    private fun disposeAssetLoader() {
        if (assetLoaderDisposed) return
        assetLoader.dispose()
        assetLoaderDisposed = true
    }

    private fun loadModel(request: ModelCompositionRequest): Promise<ThreeModelAsset> {
        val loaded = runCatching { assetLoader.loadModel(request.modelBase, request.source) }.getOrElse { error ->
            return rejectedPromise(HostFailure(MODEL_ASSET_FAILED, "模型资产加载失败：${safeMessage(error)}"))
        }
        return loaded.catch { reason ->
            throw HostFailure(MODEL_ASSET_FAILED, "模型资产加载失败：${safeReason(reason)}")
        }
    }

    private fun prepareBundle(
        request: ModelCompositionRequest,
        record: InstanceRecord,
        serial: Int,
        capturedEpoch: Int,
        asset: ThreeModelAsset,
    ): Promise<PreparedRender> {
        requireActive(record, serial, capturedEpoch)
        val key = request.bundleKey(asset)
        val viewState = effectiveViewState(request, record)
        record.cached?.takeIf { it.key == key && it.ownsBundle }?.let { cached ->
            cached.viewState = viewState
            return Promise.resolve(PreparedRender(request, record, cached, serial, capturedEpoch))
        }
        val parsed = parseModel(request, asset)
        return loadModule().thenPromise { module ->
            ThreeSceneBuilder(module, assetLoader).build(asset, parsed).then { candidate ->
                acceptCandidate(request, record, key, viewState, module, candidate, serial, capturedEpoch)
            }
        }
    }

    private fun parseModel(request: ModelCompositionRequest, asset: ThreeModelAsset): RestrictedBbModel {
        val override = request.texture?.let { RestrictedBbTextureOverride(request.modelBase, it) }
        return RestrictedBbModelParser.parse(asset.text, asset.canonicalPath, override)
    }

    private fun loadModule(): Promise<dynamic> {
        val loaded = runCatching(moduleLoader::load).getOrElse { error ->
            return rejectedPromise(HostFailure(MODULE_IMPORT_FAILED, "Three 动态模块加载失败：${safeMessage(error)}"))
        }
        return loaded.catch { reason ->
            throw HostFailure(MODULE_IMPORT_FAILED, "Three 动态模块或代码分块加载失败：${safeReason(reason)}")
        }
    }

    private fun acceptCandidate(
        request: ModelCompositionRequest,
        record: InstanceRecord,
        key: ThreeModelBundleKey,
        viewState: ThreeModelViewState,
        module: dynamic,
        candidate: ThreeSceneBundle,
        serial: Int,
        capturedEpoch: Int,
    ): PreparedRender {
        try {
            requireActive(record, serial, capturedEpoch)
        } catch (error: Throwable) {
            candidate.dispose()
            throw error
        }
        val previous = record.cached
        val cached = CachedBundle(key, module, candidate, viewState, ownsBundle = true)
        record.cached = cached
        previous?.takeIf(CachedBundle::ownsBundle)?.bundle?.dispose()
        return PreparedRender(request, record, cached, serial, capturedEpoch)
    }

    private fun acceptPrepared(
        request: ModelCompositionRequest,
        record: InstanceRecord,
        entry: ThreePreparedModelEntry,
        serial: Int,
        capturedEpoch: Int,
    ): PreparedRender {
        requireActive(record, serial, capturedEpoch)
        val previous = record.cached
        val view = effectiveViewState(request, record)
        val cached = CachedBundle(entry.key, entry.module, entry.bundle, view, ownsBundle = false)
        record.cached = cached
        previous?.takeIf(CachedBundle::ownsBundle)?.bundle?.dispose()
        return PreparedRender(request, record, cached, serial, capturedEpoch)
    }

    private fun effectiveViewState(request: ModelCompositionRequest, record: InstanceRecord): ThreeModelViewState =
        ThreeModelViewState(
            request.rotationX + record.viewOffsetX,
            request.rotationY + record.viewOffsetY,
        )

    /** source/key 变化时归零视角偏移，避免旧交互污染新模型。 */
    private fun syncViewOffsetIdentity(record: InstanceRecord, request: ModelCompositionRequest) {
        val identity = ViewIdentity(request.source, request.texture, request.modelBase)
        if (record.viewIdentity == identity) return
        record.viewIdentity = identity
        record.viewOffsetX = 0f
        record.viewOffsetY = 0f
    }

    private fun syncHitRegistration(record: InstanceRecord, request: ModelCompositionRequest) {
        val registry = hitRegistry
        if (!request.interactive || registry == null) {
            unregisterHit(record)
            return
        }
        val owner = record.hitOwner ?: ModelPreviewHitOwner { dx, dy ->
            // 屏幕 X 拖拽映射到 yaw（Y 轴旋转），Y 拖拽映射到 pitch
            applyViewOffset(request.instanceKey, dy * VIEW_DRAG_SENSITIVITY, dx * VIEW_DRAG_SENSITIVITY)
        }.also { record.hitOwner = it }
        owner.updateRegion(request.rect)
        if (record.hitRegistration == null) {
            record.hitRegistration = registry.register(owner, MODEL_HIT_Z_INDEX, owner.hitRegion())
        }
    }

    private fun unregisterHit(record: InstanceRecord) {
        record.hitRegistration?.unregister()
        record.hitRegistration = null
        record.hitOwner = null
    }

    private fun clearHitRegistrations() {
        instances.values.forEach(::unregisterHit)
    }

    private fun activatePrepared(prepared: ThreePreparedModelSet): ThreePreparedModelSet? {
        check(!disposed) { "模型合成宿主已销毁" }
        val previous = activePreparedModels
        activePreparedModels = prepared
        return previous
    }

    private fun replacePrepared(expected: ThreePreparedModelSet, replacement: ThreePreparedModelSet?) {
        if (activePreparedModels === expected) activePreparedModels = replacement
    }

    private fun enqueueRender(prepared: PreparedRender): Promise<ModelCompositionResult> {
        val next = renderQueue.thenPromise { render(prepared) }
        renderQueue = next.then(onFulfilled = { Unit }, onRejected = { Unit })
        return next
    }

    private fun render(prepared: PreparedRender): Promise<ModelCompositionResult> = Promise.resolve(
        runCatching {
            requireActive(prepared.record, prepared.serial, prepared.epoch)
            require(prepared.record.cached === prepared.cached)
            require(prepared.cached.ownsBundle || activePreparedModels?.owns(prepared.cached.bundle) == true)
            renderReady(prepared)
        }.getOrElse { throw it },
    )

    private fun renderReady(prepared: PreparedRender): ModelCompositionResult.Ready {
        val request = prepared.request
        val dimensions = outputDimensions(request)
        applyViewAndCamera(prepared.cached, dimensions, request.scale)
        val state = ensureRenderer(prepared.cached.module)
        val pixels = renderPixels(state, prepared.cached.bundle, dimensions, request.backgroundColor)
        val rawLease = createOutputLease(dimensions, pixels)
        lateinit var tracked: TrackedOutputLease
        tracked = TrackedOutputLease(rawLease) { outputLeases.remove(tracked) }
        outputLeases += tracked
        return ModelCompositionResult.Ready(tracked)
    }

    private fun outputDimensions(request: ModelCompositionRequest): OutputDimensions {
        val width = finiteDimension(request.rect.width, "宽度")
        val height = finiteDimension(request.rect.height, "高度")
        val byteCount = width.toLong() * height.toLong() * RGBA_CHANNELS
        if (byteCount > Int.MAX_VALUE) {
            throw HostFailure(OUTPUT_FAILED, "模型输出像素数量过大")
        }
        return OutputDimensions(width, height, byteCount.toInt())
    }

    private fun finiteDimension(value: Float, name: String): Int {
        if (!value.isFinite() || value <= 0f) {
            throw HostFailure(OUTPUT_FAILED, "模型输出${name}必须是正有限数值")
        }
        return value.roundToInt().coerceAtLeast(1)
    }

    private fun applyViewAndCamera(cached: CachedBundle, dimensions: OutputDimensions, requestedScale: Float) {
        val bundle = cached.bundle
        val view = cached.viewState
        bundle.viewRoot.rotation.set(view.rotationX.toRadians(), view.rotationY.toRadians(), 0.0)
        bundle.viewRoot.updateMatrixWorld(true)
        val size = construct(cached.module.Vector3)
        construct(cached.module.Box3).setFromObject(bundle.viewRoot).getSize(size)
        fitCamera(bundle.camera, size, dimensions, normalizedScale(requestedScale))
    }

    private fun fitCamera(camera: dynamic, size: dynamic, dimensions: OutputDimensions, scale: Double) {
        val aspect = dimensions.width.toDouble() / dimensions.height.toDouble()
        val modelWidth = positiveSize(size.x as Double)
        val modelHeight = positiveSize(size.y as Double)
        val modelDepth = positiveSize(size.z as Double)
        val verticalHalf = max(modelHeight / 2.0, modelWidth / (2.0 * aspect)) * FIT_PADDING
        val radius = sqrt(modelWidth * modelWidth + modelHeight * modelHeight + modelDepth * modelDepth) / 2.0
        camera.left = -verticalHalf * aspect
        camera.right = verticalHalf * aspect
        camera.top = verticalHalf
        camera.bottom = -verticalHalf
        camera.zoom = scale
        camera.near = CAMERA_NEAR
        camera.far = radius * CAMERA_FAR_MULTIPLIER + CAMERA_DISTANCE_BASE
        camera.position.set(0.0, 0.0, radius * CAMERA_DISTANCE_MULTIPLIER + CAMERA_DISTANCE_BASE)
        camera.lookAt(0.0, 0.0, 0.0)
        camera.updateProjectionMatrix()
    }

    private fun ensureRenderer(module: dynamic): RendererState {
        rendererState?.let { return it }
        if (contextLost) throw HostFailure(CONTEXT_LOST, "WebGL 上下文尚未恢复")
        return try {
            createRendererState(module).also { rendererState = it }
        } catch (error: Throwable) {
            throw HostFailure(WEBGL_FAILED, "Three WebGLRenderer 初始化失败：${safeMessage(error)}")
        }
    }

    private fun createRendererState(module: dynamic): RendererState {
        val canvas = renderCanvasFactory.create()
        var renderer: dynamic = null
        var target: dynamic = null
        return try {
            renderer = construct(
                module.WebGLRenderer,
                json(
                    "canvas" to canvas,
                    "alpha" to true,
                    "antialias" to true,
                    "premultipliedAlpha" to true,
                    "preserveDrawingBuffer" to false,
                ),
            )
            renderer.outputColorSpace = module.SRGBColorSpace
            target = construct(
                module.WebGLRenderTarget,
                1,
                1,
                json("format" to module.RGBAFormat, "type" to module.UnsignedByteType, "depthBuffer" to true),
            )
            registerContextListeners(module, renderer, target, canvas)
        } catch (error: Throwable) {
            disposePartialRenderer(renderer, target)
            throw error
        }
    }

    private fun disposePartialRenderer(renderer: dynamic, target: dynamic) {
        if (target != null) runCatching { target.dispose() }
        if (renderer == null) return
        runCatching { renderer.renderLists.dispose() }
        runCatching { renderer.dispose() }
    }

    private fun registerContextListeners(
        module: dynamic,
        renderer: dynamic,
        target: dynamic,
        canvas: dynamic,
    ): RendererState {
        lateinit var state: RendererState
        val lost: (dynamic) -> Unit = { event -> handleContextLost(state, event) }
        val restored: (dynamic) -> Unit = { handleContextRestored(state) }
        state = RendererState(module, renderer, target, canvas, lost, restored)
        return try {
            canvas.addEventListener(CONTEXT_LOST_EVENT, lost)
            canvas.addEventListener(CONTEXT_RESTORED_EVENT, restored)
            state
        } catch (error: Throwable) {
            state.detach()
            throw error
        }
    }

    private fun renderPixels(
        state: RendererState,
        bundle: ThreeSceneBundle,
        dimensions: OutputDimensions,
        backgroundColor: Int,
    ): dynamic = try {
        state.target.setSize(dimensions.width, dimensions.height)
        state.renderer.setSize(dimensions.width, dimensions.height, false)
        state.renderer.setClearColor(backgroundColor and RGB_MASK, alpha(backgroundColor))
        state.renderer.setRenderTarget(state.target)
        state.renderer.render(bundle.scene, bundle.camera)
        backendSubmissionObserver()
        val pixels = uint8Array(dimensions.byteCount)
        state.renderer.readRenderTargetPixels(
            state.target,
            0,
            0,
            dimensions.width,
            dimensions.height,
            pixels,
        )
        state.renderer.setRenderTarget(null)
        flipRows(pixels, dimensions.width, dimensions.height)
    } catch (error: Throwable) {
        runCatching { state.renderer.setRenderTarget(null) }
        if (contextLost) throw HostFailure(CONTEXT_LOST, "WebGL 上下文在模型渲染期间丢失")
        throw HostFailure(RENDER_FAILED, "Three 模型渲染或像素回读失败：${safeMessage(error)}")
    }

    private fun createOutputLease(dimensions: OutputDimensions, pixels: dynamic): ModelTextureLease = try {
        outputSequence += 1
        outputFactory.create(
            dimensions.width,
            dimensions.height,
            pixels,
            "three-model-$epoch-$outputSequence",
        )
    } catch (error: Throwable) {
        throw HostFailure(OUTPUT_FAILED, "Pixi 模型输出纹理创建失败：${safeMessage(error)}")
    }

    private fun requireActive(record: InstanceRecord, serial: Int, capturedEpoch: Int) {
        if (disposed) throw HostFailure(CANCELLED, "模型合成宿主已销毁")
        if (capturedEpoch != epoch || contextLost) throw HostFailure(CONTEXT_LOST, "WebGL 上下文已变化")
        if (instances.values.none { it === record } || record.serial != serial) {
            throw HostFailure(CANCELLED, "模型合成请求已被更新请求替代")
        }
    }

    private fun handleContextLost(state: RendererState, event: dynamic) {
        runCatching { event.preventDefault() }
        if (disposed || rendererState !== state) return
        contextLost = true
        epoch += 1
        rendererState = null
        disposeOutputLeases()
        disposeInstances()
        state.disposeRenderer()
        lostCanvas = LostCanvas(state.canvas, state.lostListener, state.restoredListener)
        lastRequest?.let { emitPlaceholder(it, CONTEXT_LOST, "WebGL 上下文已丢失，Three 模型预览已降级为占位") }
    }

    private fun handleContextRestored(state: RendererState) {
        val lost = lostCanvas ?: return
        if (lost.canvas !== state.canvas) return
        lost.detach()
        lostCanvas = null
        if (!disposed) contextLost = false
    }

    private fun failureResult(request: ModelCompositionRequest, reason: dynamic): ModelCompositionResult {
        val error = reason as? Throwable ?: RuntimeException(safeReason(reason))
        if (error is HostFailure && error.code == CANCELLED) return ModelCompositionResult.Cancelled
        val failure = classifyFailure(error)
        emitPlaceholder(request, failure.code, failure.message ?: "Three 模型合成失败")
        return ModelCompositionResult.Placeholder(failure.code)
    }

    private fun classifyFailure(error: Throwable): HostFailure = when (error) {
        is HostFailure -> error
        is RestrictedBbModelException -> parserFailure(error)
        is ThreeModelBuildException -> HostFailure(error.code, error.message ?: "Three 模型场景构造失败")
        else -> HostFailure(RENDER_FAILED, "Three 模型合成失败：${safeMessage(error)}")
    }

    private fun parserFailure(error: RestrictedBbModelException): HostFailure {
        val message = if (error.code == BBMODEL_JSON_INVALID) {
            "模型 JSON 解析失败：${error.message}"
        } else {
            "模型 JSON 结构校验失败：${error.message}"
        }
        return HostFailure(MODEL_JSON_FAILED, message)
    }

    private fun contextPlaceholder(request: ModelCompositionRequest): ModelCompositionResult.Placeholder {
        emitPlaceholder(request, CONTEXT_LOST, "WebGL 上下文尚未恢复，Three 模型预览使用占位")
        return ModelCompositionResult.Placeholder(CONTEXT_LOST)
    }

    private fun emitPlaceholder(request: ModelCompositionRequest, code: String, message: String) {
        diagnostics.emit(
            WebRenderDiagnostic(
                code,
                WebRenderDiagnosticSeverity.ERROR,
                message,
                request.sessionId,
                request.generation,
                WebRenderDiagnosticCapabilityKeys.MODEL_COMPOSITION,
                canonicalWebResourceKey(request.source),
                request.phase,
                request.sequence,
            ),
        )
    }

    private fun disposeInstances() {
        instances.values.forEach { record ->
            unregisterHit(record)
            record.cached?.takeIf(CachedBundle::ownsBundle)?.bundle?.dispose()
        }
        instances.clear()
    }

    private fun disposeOutputLeases() {
        outputLeases.toList().forEach(TrackedOutputLease::dispose)
        outputLeases.clear()
    }

    private fun disposeRendererState() {
        val state = rendererState ?: return
        rendererState = null
        state.detach()
        state.disposeRenderer()
    }
}

private class InstanceRecord {
    var serial = 0
    var cached: CachedBundle? = null
    var viewOffsetX = 0f
    var viewOffsetY = 0f
    var viewIdentity: ViewIdentity? = null
    var hitOwner: ModelPreviewHitOwner? = null
    var hitRegistration: HitRegistration? = null
}

private data class ViewIdentity(
    val source: String,
    val texture: String?,
    val modelBase: String,
)

private data class CachedBundle(
    val key: ThreeModelBundleKey,
    val module: dynamic,
    val bundle: ThreeSceneBundle,
    var viewState: ThreeModelViewState,
    val ownsBundle: Boolean,
)

private data class PreparedRender(
    val request: ModelCompositionRequest,
    val record: InstanceRecord,
    val cached: CachedBundle,
    val serial: Int,
    val epoch: Int,
)

private data class OutputDimensions(
    val width: Int,
    val height: Int,
    val byteCount: Int,
)

private class RendererState(
    val module: dynamic,
    val renderer: dynamic,
    val target: dynamic,
    val canvas: dynamic,
    val lostListener: (dynamic) -> Unit,
    val restoredListener: (dynamic) -> Unit,
) {
    private var rendererDisposed = false

    fun detach() {
        runCatching { canvas.removeEventListener(CONTEXT_LOST_EVENT, lostListener) }
        runCatching { canvas.removeEventListener(CONTEXT_RESTORED_EVENT, restoredListener) }
    }

    fun disposeRenderer() {
        if (rendererDisposed) return
        rendererDisposed = true
        runCatching { target.dispose() }
        runCatching { renderer.renderLists.dispose() }
        runCatching { renderer.dispose() }
    }
}

private class LostCanvas(
    val canvas: dynamic,
    private val lostListener: (dynamic) -> Unit,
    private val restoredListener: (dynamic) -> Unit,
) {
    fun detach() {
        runCatching { canvas.removeEventListener(CONTEXT_LOST_EVENT, lostListener) }
        runCatching { canvas.removeEventListener(CONTEXT_RESTORED_EVENT, restoredListener) }
    }
}

private class TrackedOutputLease(
    private val delegate: ModelTextureLease,
    private val onDisposed: () -> Unit,
) : ModelTextureLease {
    private var disposed = false

    override val leaseId: String
        get() = delegate.leaseId

    override fun onTextureReady(callback: (Texture) -> Unit) {
        if (!disposed) delegate.onTextureReady(callback)
    }

    override fun dispose() {
        if (disposed) return
        disposed = true
        runCatching(delegate::dispose)
        onDisposed()
    }
}

private object BrowserThreeModelOutputFactory : ThreeModelOutputFactory {
    override fun create(width: Int, height: Int, rgbaPixels: dynamic, leaseId: String): ModelTextureLease {
        val canvas = createOutputCanvas(width, height)
        val context = canvas.getContext("2d")
            ?: throw HostFailure(OUTPUT_FAILED, "浏览器未提供二维画布上下文")
        val image = context.createImageData(width, height)
        image.data.set(rgbaPixels)
        context.putImageData(image, 0, 0)
        val texture = Texture.from(canvas)
        return BrowserOutputLease(leaseId, texture, canvas)
    }
}

private class BrowserOutputLease(
    override val leaseId: String,
    private val texture: Texture,
    private val canvas: dynamic,
) : ModelTextureLease {
    private var disposed = false

    override fun onTextureReady(callback: (Texture) -> Unit) {
        if (!disposed) callback(texture)
    }

    override fun dispose() {
        if (disposed) return
        disposed = true
        runCatching { texture.destroy(true) }
        canvas.width = 0
        canvas.height = 0
    }
}

private class HostFailure(
    val code: String,
    message: String,
) : RuntimeException(message)

private fun ModelCompositionRequest.staticModelRequest(): ThreeStaticModelRequest =
    ThreeStaticModelRequest(modelBase, source, texture)

private fun ModelCompositionRequest.bundleKey(asset: ThreeModelAsset): ThreeModelBundleKey = ThreeModelBundleKey(
    source,
    texture,
    modelBase,
    asset.canonicalPath,
    asset.contentVersion,
)

private fun createRenderCanvas(): dynamic = when {
    js("typeof document !== 'undefined'") as Boolean -> js("document.createElement('canvas')")
    js("typeof OffscreenCanvas !== 'undefined'") as Boolean -> js("new OffscreenCanvas(1, 1)")
    else -> throw HostFailure(WEBGL_FAILED, "当前环境无法创建离屏 WebGL 画布")
}

private fun createOutputCanvas(width: Int, height: Int): dynamic {
    val canvas = when {
        js("typeof document !== 'undefined'") as Boolean -> js("document.createElement('canvas')")
        js("typeof OffscreenCanvas !== 'undefined'") as Boolean -> js("new OffscreenCanvas(width, height)")
        else -> throw HostFailure(OUTPUT_FAILED, "当前环境无法创建离屏输出画布")
    }
    canvas.width = width
    canvas.height = height
    return canvas
}

private fun construct(constructor: dynamic, vararg args: dynamic): dynamic =
    js("Reflect.construct(constructor, args)")

private val uint8ArrayFactory: dynamic = js("(function(size) { return new Uint8Array(size); })")

private fun uint8Array(size: Int): dynamic = uint8ArrayFactory(size)

private val flipRowsFactory: dynamic = js(
    """
    (function(pixels, width, height) {
      var rowBytes = width * 4;
      var flipped = new Uint8Array(pixels.length);
      for (var y = 0; y < height; y++) {
        var source = (height - 1 - y) * rowBytes;
        flipped.set(pixels.subarray(source, source + rowBytes), y * rowBytes);
      }
      return flipped;
    })
    """,
)

private fun flipRows(pixels: dynamic, width: Int, height: Int): dynamic = flipRowsFactory(pixels, width, height)

private fun Float.toRadians(): Double = toDouble() * PI / HALF_TURN_DEGREES

private fun normalizedScale(value: Float): Double =
    if (value.isFinite() && value > 0f) value.toDouble() else 1.0

private fun positiveSize(value: Double): Double =
    if (value.isFinite() && value > MIN_MODEL_SIZE) value else MIN_MODEL_SIZE

private fun alpha(argb: Int): Double = ((argb ushr 24) and 0xFF) / 255.0

private fun safeMessage(error: Throwable): String = error.message?.takeIf(String::isNotBlank) ?: "未知错误"

private fun safeReason(reason: dynamic): String = reason?.toString()?.takeIf(String::isNotBlank) ?: "未知错误"

private fun <T> rejectedPromise(error: Throwable): Promise<T> = Promise { _, reject -> reject(error) }

private fun <T, R> Promise<T>.thenPromise(transform: (T) -> Promise<R>): Promise<R> =
    then(transform).unsafeCast<Promise<R>>()

private const val BBMODEL_JSON_INVALID = "BBMODEL_JSON_INVALID"
private const val MODEL_ASSET_FAILED = "THREE_MODEL_ASSET_FAILED"
private const val MODULE_IMPORT_FAILED = "THREE_MODEL_MODULE_FAILED"
private const val MODEL_JSON_FAILED = "THREE_MODEL_JSON_FAILED"
private const val WEBGL_FAILED = "THREE_MODEL_WEBGL_FAILED"
private const val RENDER_FAILED = "THREE_MODEL_RENDER_FAILED"
private const val OUTPUT_FAILED = "THREE_MODEL_OUTPUT_FAILED"
private const val CONTEXT_LOST = "THREE_MODEL_CONTEXT_LOST"
private const val CANCELLED = "THREE_MODEL_CANCELLED"
private const val CONTEXT_LOST_EVENT = "webglcontextlost"
private const val CONTEXT_RESTORED_EVENT = "webglcontextrestored"
private const val HALF_TURN_DEGREES = 180.0
private const val FIT_PADDING = 1.08
private const val CAMERA_NEAR = 0.01
private const val CAMERA_DISTANCE_MULTIPLIER = 3.0
private const val CAMERA_FAR_MULTIPLIER = 8.0
private const val CAMERA_DISTANCE_BASE = 1.0
private const val MIN_MODEL_SIZE = 0.001
private const val RGBA_CHANNELS = 4L
private const val RGB_MASK = 0xFFFFFF
private const val MODEL_HIT_Z_INDEX = 100
private const val VIEW_DRAG_SENSITIVITY = 0.4f
